const DEFAULT_ERROR_MESSAGE = "Something went wrong.";

export type AppErrorKind = "api" | "validation" | "network" | "unknown";

export interface AppError {
  message: string;
  status: number | null;
  details: unknown;
  payload: unknown;
  kind: AppErrorKind;
}

export interface NormalizeErrorOptions {
  fallbackMessage?: string;
  kind?: AppErrorKind;
}

export class ApiResponseError extends Error {
  readonly payload: unknown;

  readonly status: number;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiResponseError";
    this.status = status;
    this.payload = payload;
  }
}

class ExceptionAdapter {
  static async resolve<T>(
    fn: () => Promise<T>,
    fallback: T,
    onError?: (error: unknown) => void,
  ): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      onError?.(error);
      return fallback;
    }
  }

  static normalize(error: unknown, options?: NormalizeErrorOptions): AppError {
    const fallbackMessage = options?.fallbackMessage ?? DEFAULT_ERROR_MESSAGE;
    const status = this.extractStatus(error);
    const payload = error instanceof ApiResponseError ? error.payload : error;
    const details = this.getDetails(error);
    const message = this.getMessage(error, fallbackMessage);

    return {
      message,
      status,
      details,
      payload,
      kind: options?.kind ?? this.resolveKind(error, status, details),
    };
  }

  static getDetails(error: unknown): unknown {
    if (error instanceof ApiResponseError) {
      return this.extractDetails(error.payload);
    }

    if (this.isRecord(error)) {
      return this.extractDetails(error);
    }

    return undefined;
  }

  static getMessage(error: unknown, fallbackMessage = DEFAULT_ERROR_MESSAGE): string {
    if (error instanceof Error && error.message.trim()) {
      return error.message;
    }

    if (typeof error === "string" && error.trim()) {
      return error;
    }

    const nestedMessage = this.extractFromRecord(error);
    return nestedMessage ?? fallbackMessage;
  }

  private static resolveKind(
    error: unknown,
    status: number | null,
    details: unknown,
  ): AppErrorKind {
    if (status !== null) {
      if (status === 0) {
        return "network";
      }

      if (status === 400 || status === 422 || details !== undefined) {
        return "validation";
      }

      return "api";
    }

    if (
      this.isRecord(error) &&
      typeof error.code === "string" &&
      error.code.toLowerCase().includes("network")
    ) {
      return "network";
    }

    return "unknown";
  }

  private static extractStatus(error: unknown): number | null {
    if (error instanceof ApiResponseError) {
      return error.status;
    }

    if (!this.isRecord(error)) {
      return null;
    }

    if (typeof error.status === "number") {
      return error.status;
    }

    if (this.isRecord(error.response) && typeof error.response.status === "number") {
      return error.response.status;
    }

    return null;
  }

  private static extractFromRecord(error: unknown): string | undefined {
    if (!this.isRecord(error)) {
      return undefined;
    }

    const directMessage = this.firstString(error.message);
    if (directMessage) {
      return directMessage;
    }

    const detailMessage = this.firstString(error.detail);
    if (detailMessage) {
      return detailMessage;
    }

    const errorMessage = this.firstString(error.error);
    if (errorMessage) {
      return errorMessage;
    }

    if (this.isRecord(error.response) && "data" in error.response) {
      return this.extractFromRecord(error.response.data);
    }

    return undefined;
  }

  private static extractDetails(error: unknown): unknown {
    if (!this.isRecord(error)) {
      return undefined;
    }

    if ("details" in error) {
      return error.details;
    }

    if (this.isRecord(error.response) && "data" in error.response) {
      return this.extractDetails(error.response.data);
    }

    return undefined;
  }

  private static firstString(value: unknown): string | undefined {
    if (typeof value === "string" && value.trim()) {
      return value;
    }

    if (Array.isArray(value)) {
      const firstItem = value.find(
        (item): item is string => typeof item === "string" && item.trim().length > 0,
      );

      if (firstItem) {
        return firstItem;
      }
    }

    return undefined;
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }
}

export default ExceptionAdapter;
